'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Share } from 'lucide-react';
import { Button, ButtonGroup, IconButton } from '@zillow/constellation-10';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { FavoriteHeartIcon } from '@/src/components/features/listings/FavoriteHeartIcon';
import { cx } from '@/styled-system/css';
import { useShareAction } from '@/src/lib/hooks/useShareAction';
import { useListingCTAs, useListingSaveActions } from '@/src/components/features/property/hooks';
import { useListingActions } from '@/src/components/features/listing-actions';
import { ListingActionMenu } from '@/src/components/features/listings/ListingActionMenu';
import { listingCardActions } from '@/src/components/ListingCard/listingCard.styles';
import {
  headerActionButton,
  headerCtaButton,
  headerCtaButtonLabel,
  headerIconButton,
  mobileActionGroup,
  utilityActionButton,
  utilityActionButtonLabel,
} from '@/src/components/features/property/ListingPage/desktopStickyHeader.styles';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';

type ListingHeaderActionsProps = {
  listing: PropertyDetailListing;
  showCTAs?: boolean;
  presentation?: 'responsive' | 'mobileFloating';
  mobileFloatingVariant?: 'onMedia' | 'onSurface';
  /** Legacy prop kept for back-compat; the new pill layout uses fixed dimensions. */
  size?: 'sm' | 'md';
};

/**
 * ListingHeaderActions — the top-right action cluster on the desktop HDP
 * sticky header. Composes Save / Share icon-with-label buttons and (when
 * `showCTAs` is true) "Book tour" / "Message" pill CTAs.
 */
export const ListingHeaderActions = ({ listing, showCTAs, presentation = 'responsive' }: ListingHeaderActionsProps) => {
  const t = useTranslations('hdp');
  const favoriteButtonRef = useRef<HTMLButtonElement>(null);
  const { listingSaved, toggleSave, identityResolved } = useListingSaveActions(listing, favoriteButtonRef);
  const { openShare, shareFormOverlay } = useShareAction(listing as ExtendedListing | null | undefined);
  const { listingHidden, toggleHide, openReport } = useListingActions(listing);
  const { ctas, overlays } = useListingCTAs(listing);
  const headerCTAs = showCTAs ? ctas : [];
  const shareLabel = t('share');
  const shareAriaLabel = t('shareThisListing');
  const saveLabel = t('save');
  const saveAriaLabel = listingSaved ? t('removeFromSaved') : t('saveThisListing');

  if (presentation === 'mobileFloating') {
    return (
      <>
        <ButtonGroup
          role="group"
          aria-label={t('listingOptions')}
          tone="neutral-fixed"
          emphasis="bare"
          size="sm"
          className={mobileActionGroup}
          css={listingCardActions}
        >
          <IconButton
            ref={favoriteButtonRef}
            type="button"
            shape="circle"
            title={saveLabel}
            aria-label={saveAriaLabel}
            className={headerIconButton}
            onClick={toggleSave}
            disabled={!identityResolved}
            aria-busy={!identityResolved}
            aria-pressed={listingSaved}
            icon={<FavoriteHeartIcon selected={listingSaved} />}
          />

          <IconButton
            type="button"
            shape="circle"
            title={shareLabel}
            aria-label={shareAriaLabel}
            className={headerIconButton}
            onClick={openShare}
            icon={<LucideIcon icon={Share} />}
          />

          <ListingActionMenu
            listingHidden={listingHidden}
            onHide={toggleHide}
            onReport={openReport}
            tone="neutral-fixed"
            emphasis="bare"
            size="sm"
            className={headerIconButton}
          />
        </ButtonGroup>

        {showCTAs && overlays}
        {shareFormOverlay}
      </>
    );
  }

  const saveIcon = <FavoriteHeartIcon selected={listingSaved} />;
  const shareIcon = <LucideIcon icon={Share} size="sm" />;

  return (
    <>
      {headerCTAs.map((cta) => {
        return (
          <Button.Root
            key={cta.id}
            type="button"
            tone="brand"
            emphasis={cta.emphasis}
            size="sm"
            className={headerCtaButton}
            onClick={cta.onClick}
            disabled={cta.disabled}
            aria-busy={cta.ariaBusy}
            aria-label={cta.ariaLabel}
            title={cta.label}
            // Server-rendered, so HTTP monitors can assert it. Distinct from InlineCTAs'
            // `button-<id>` so the two never collide once both are mounted.
            data-testid={`hdp-header-cta-${cta.id}`}
          >
            {cta.icon && <Button.Icon>{cta.icon}</Button.Icon>}
            <Button.Label className={cta.icon ? headerCtaButtonLabel : undefined}>{cta.label}</Button.Label>
          </Button.Root>
        );
      })}

      <Button.Root
        ref={favoriteButtonRef}
        type="button"
        tone="neutral"
        emphasis="bare"
        size="sm"
        className={cx(headerActionButton, utilityActionButton)}
        onClick={toggleSave}
        disabled={!identityResolved}
        aria-busy={!identityResolved}
        aria-pressed={listingSaved}
        aria-label={saveAriaLabel}
        title={saveLabel}
      >
        <Button.Label className={utilityActionButtonLabel}>{saveLabel}</Button.Label>
        <Button.Icon>{saveIcon}</Button.Icon>
      </Button.Root>

      <Button.Root
        type="button"
        tone="neutral"
        emphasis="bare"
        size="sm"
        className={cx(headerActionButton, utilityActionButton)}
        onClick={openShare}
        aria-label={shareAriaLabel}
        title={shareLabel}
      >
        <Button.Label className={utilityActionButtonLabel}>{shareLabel}</Button.Label>
        <Button.Icon>{shareIcon}</Button.Icon>
      </Button.Root>

      <ListingActionMenu
        listingHidden={listingHidden}
        onHide={toggleHide}
        onReport={openReport}
        className={headerIconButton}
      />

      {showCTAs && overlays}
      {shareFormOverlay}
    </>
  );
};
